import {
    createUserWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    OAuthProvider,
    FacebookAuthProvider,
    getAdditionalUserInfo,
    updateProfile,
} from "firebase/auth";
import { getFirebaseAuth } from "../firebase";
import {
    getItemFromLocalStorage,
    removeItemFromLocalStorage,
    setItemToLocalStorage,
} from "../utils/localStorage";
import { TOKEN } from "../constants/const";

export const userRegistration = async (values) => {
    const auth = getFirebaseAuth();
    try {
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            values.email,
            values.password
        );

        if (!userCredential.user.emailVerified) {
            const shouldReturnToPayment =
                typeof window !== "undefined" &&
                new URLSearchParams(window.location.search).get("payment") ===
                    "true";
            const payment = shouldReturnToPayment ? "?payment=true" : "";
            await sendEmailVerification(userCredential.user, {
                url: `${location.origin}/auth/login${payment}`,
            });
        }

        const firebaseUser = userCredential.user;
        await updateProfile(firebaseUser, {
            displayName: values.email,
        });

        // No force-refresh needed — the account was just created, so the
        // token Firebase already holds is current. Forcing one here just
        // adds a redundant network round trip.
        return { token: await firebaseUser.getIdToken() };
    } catch (error) {
        throw error;
    }
};

export const updateUserReferralCode = async (values) => {
    try {
        const auth = getFirebaseAuth();
        const data = await signInWithEmailAndPassword(
            auth,
            values.email,
            values.password
        );
        const token = await data.user.getIdToken(true);
        const res = await fetch(
            `${process.env.GATSBY_API_URL}/login?referralCode=${values.referralCode}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-auth-token": `Bearer ${token}`,
                },
            }
        );
        return await res.json();
    } catch (error) {
        throw error;
    }
};

export const userLogin = async (values) => {
    try {
        const auth = getFirebaseAuth();
        const data = await signInWithEmailAndPassword(
            auth,
            values.email,
            values.password
        );
        if (!data.user.emailVerified) {
            await auth.signOut();
            throw new Error(
                "Your email address has not been verified yet. Please check your inbox and click the verification link to activate your account."
            );
        }
        const token = await data.user.getIdToken(true);
        const res = await fetch(`${process.env.GATSBY_API_URL}/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-auth-token": `Bearer ${token}`,
            },
        });
        setItemToLocalStorage(TOKEN, token);
        return await res.json();
    } catch (error) {
        throw error;
    }
};

export const recoverPassword = async (values) => {
    try {
        const auth = getFirebaseAuth();
        await sendPasswordResetEmail(auth, values.email);
    } catch (error) {
        throw error;
    }
};

export const locationVerification = async () => {
    try {
        const res = await fetch(`${process.env.GATSBY_API_URL}/validate`);
        return await res.json();
    } catch (error) {
        console.error("Registration error:", error);
        return error;
    }
};

export const userAgeVerification = async (verification) => {
    try {
        const token = getItemFromLocalStorage(TOKEN);
        const res = await fetch(
            `${process.env.GATSBY_API_URL}/client/me/age-verification`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-auth-token": `Bearer ${token}`,
                },
                body: JSON.stringify(verification),
            }
        );

        return await res.json();
    } catch (error) {
        throw error;
    }
};

export const acceptPrivacyTerms = async () => {
    try {
        const token = getItemFromLocalStorage(TOKEN);
        const res = await fetch(
            `${process.env.GATSBY_API_URL}/client/me/privacy-terms`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "x-auth-token": `Bearer ${token}`,
                },
            }
        );

        return await res.json();
    } catch (error) {
        throw error;
    }
};

export const getUser = async () => {
    const token = getItemFromLocalStorage(TOKEN);
    if (!token) return null;

    const res = await fetch(`${process.env.GATSBY_API_URL}/client/me`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "x-auth-token": `Bearer ${token}`,
        },
        cache: "no-store",
    });
    if (!res.ok) {
        // Token is stale/invalid — drop it so we don't keep retrying.
        // Cache invalidation is handled by useUser's onError using the
        // real QueryClient from the React tree.
        removeItemFromLocalStorage(TOKEN);
        throw new Error(`HTTP error! Status: ${res.status}`);
    }
    return await res.json();
};

export const getDailyBonus = async () => {
    const token = getItemFromLocalStorage(TOKEN);
    if (!token) return;
    try {
        const res = await fetch(
            `${process.env.GATSBY_API_URL}/client/me/daily-bonus`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "x-auth-token": `Bearer ${token}`,
                },
                cache: "no-store",
            }
        );
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        return await res.json();
    } catch (e) {
        throw e;
    }
};

/**
 * SSO providers supported by the platform. The string key is what the UI
 * passes to ssoSignIn; the value is the configured Firebase provider.
 */
export const SSO_PROVIDERS = {
    google: "google",
    apple: "apple",
    facebook: "facebook",
};

const buildProvider = (provider) => {
    switch (provider) {
        case SSO_PROVIDERS.google: {
            const p = new GoogleAuthProvider();
            p.addScope("email");
            p.addScope("profile");
            return p;
        }
        case SSO_PROVIDERS.apple: {
            const p = new OAuthProvider("apple.com");
            p.addScope("email");
            p.addScope("name");
            return p;
        }
        case SSO_PROVIDERS.facebook: {
            const p = new FacebookAuthProvider();
            p.addScope("email");
            return p;
        }
        default:
            throw new Error(`Unsupported SSO provider: ${provider}`);
    }
};

/**
 * Run the Firebase popup sign-in flow for the given provider. Returns the
 * authenticated user, a fresh ID token, and whether this is a brand-new
 * Firebase user — free from the same response, no extra request — so the
 * caller can skip straight to the referral popup for new signups instead of
 * making a /login call it already knows will 406.
 */
export const ssoSignIn = async (provider) => {
    const auth = getFirebaseAuth();
    const oauthProvider = buildProvider(provider);
    const credential = await signInWithPopup(auth, oauthProvider);
    const additionalInfo = getAdditionalUserInfo(credential);
    const token = await credential.user.getIdToken(true);
    return {
        user: credential.user,
        token,
        isNewUser: Boolean(additionalInfo?.isNewUser),
        provider,
    };
};

/**
 * Validates a referral code without consuming it — safe to call before
 * creating the Firebase account, so an invalid code never leaves an
 * unverified, unfinished account behind (Firebase won't let a retry reuse
 * that email once it's been used once).
 */
export const checkReferralCode = async (referralCode) => {
    try {
        return await fetch(`${process.env.GATSBY_API_URL}/referral-code`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ referralCode }),
        });
    } catch (e) {
        throw e;
    }
};

/**
 * Whether the backend currently requires a referral code to complete
 * sign-up (toggled from the dashboard's Referral Code Settings page).
 * Unauthenticated — safe to call before any sign-in attempt. Per the
 * backend contract, an environment where the setting was never touched
 * still answers (defaults to false), so a non-200 here means something's
 * actually wrong — fail toward requiring the code, since a player who
 * never sees the field when one is needed can't register at all.
 */
export const getReferralCodeRequirement = async () => {
    try {
        const res = await fetch(`${process.env.GATSBY_API_URL}/referral-code`);
        if (!res.ok) return { referralCodeRequired: true };
        return await res.json();
    } catch (e) {
        return { referralCodeRequired: true };
    }
};

/**
 * Exchange a Firebase ID token for a backend session. When a referralCode
 * is supplied (first-time SSO sign-up), it is forwarded to /login the same
 * way email/password registration does.
 */
export const completeSsoSession = async ({ token, referralCode }) => {
    const base = `${process.env.GATSBY_API_URL}/login`;
    const url = referralCode
        ? `${base}?referralCode=${encodeURIComponent(referralCode)}`
        : base;

    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-auth-token": `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        let message = `HTTP error! Status: ${res.status}`;
        try {
            const data = await res.json();
            message = data?.message || message;
        } catch (e) {
            // body wasn't JSON — keep the default message
        }
        const error = new Error(message);
        error.statusCode = res.status;
        throw error;
    }

    setItemToLocalStorage(TOKEN, token);
    return await res.json();
};

export const claimDailyBonus = async () => {
    try {
        const token = getItemFromLocalStorage(TOKEN);
        const res = await fetch(
            `${process.env.GATSBY_API_URL}/client/me/daily-bonus`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-auth-token": `Bearer ${token}`,
                },
                body: null,
            }
        );
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        return await res.json();
    } catch (e) {
        throw e;
    }
};
